// Coordinate-system readout. A small non-draggable button at the bottom-left,
// right of the gnomon nav/projection toggles, showing the icon + "GSM [RE]"
// summary. Clicking it opens a popover with the dataset/grid details — the same
// content the old draggable pane revealed when expanded.

import { raisePane } from './draggablePanels';
import type * as THREE from 'three';
import type { ViewerContext } from '../app/context';
import type { FieldMetadata } from '../physics/types';

const OVERLAY_ID = 'coordinate-system-display';
const TEXT_ID = 'coordinate-system-text';
const DETAILS_ID = 'coordinate-system-details';
const POPOVER_ID = 'coordinate-system-popover';

const fmt = (v: number): string => (Number.isInteger(v) ? `${v}` : `${(+v).toFixed(2)}`);

/** Short pill text for the collapsed view, e.g. "GSM [R_E]". The expanded
 *  view restates this inside the detail rows alongside everything else. */
const shortPillText = (coordSystem: string, units: string): string =>
    `${coordSystem || '—'} [${units || '—'}]`;

interface DisplayCtx {
    coordinateSystem: string;
    units: string;
    fieldMetadata: FieldMetadata | Partial<FieldMetadata> | null;
    dims: { nx: number; ny: number; nz: number };
    res: { dx: number; dy: number; dz: number };
    fieldUrl: string | null;
    debug?: boolean;
}

export function mountCoordinateSystemDisplay(viewer: ViewerContext): void {
    viewer.objects.axes.coordText = createCoordinateSystemDisplay(() => ({
        coordinateSystem: viewer.sceneMeta.coordinateSystem,
        units: viewer.sceneMeta.units,
        fieldMetadata: viewer.field.metadata,
        dims: { nx: viewer.grid.nx, ny: viewer.grid.ny, nz: viewer.grid.nz },
        res: { dx: viewer.grid.resolutionX, dy: viewer.grid.resolutionY, dz: viewer.grid.resolutionZ },
        fieldUrl: viewer.field.hdf5Url,
        debug: viewer.sceneMeta.debugEnabled,
    })) as unknown as THREE.Object3D | null;
}

// Older datasets stow non-DOI provenance (CCMC run IDs, bare source URLs) in the `doi`
// attribute, so linkifying it unconditionally produced dead https://doi.org/<runId> links.
// Only a DOI name or an explicit URL becomes a link; anything else renders as plain text.
function renderProvenanceRow(value: string): string {
    const isDoi = /^10\.\d{4,9}\/\S+$/.test(value);
    const isUrl = /^https?:\/\//.test(value);
    if (!isDoi && !isUrl) {
        return `<div class="coord-row"><span class="coord-key">Run ID</span><span class="coord-val">${value}</span></div>`;
    }
    const href = isDoi ? `https://doi.org/${value}` : value;
    const key = isDoi ? 'DOI' : 'Source';
    return `<div class="coord-row"><span class="coord-key">${key}</span><span class="coord-val"><a href="${href}" target="_blank" rel="noopener noreferrer">${value}</a></span></div>`;
}

function renderDetailsHTML(c: DisplayCtx): string {
    const meta = (c.fieldMetadata || {}) as Partial<FieldMetadata>;
    const gb = meta.grid_bounds;
    const { nx, ny, nz } = c.dims;
    const { dx, dy, dz } = c.res;
    const comps = Array.isArray(meta.field_components) ? meta.field_components.join(', ') : 'Unknown';
    const extX = gb ? `${fmt(gb.x.min)} to ${fmt(gb.x.max)} ${c.units}` : 'Unknown';
    const extY = gb ? `${fmt(gb.y.min)} to ${fmt(gb.y.max)} ${c.units}` : 'Unknown';
    const extZ = gb ? `${fmt(gb.z.min)} to ${fmt(gb.z.max)} ${c.units}` : 'Unknown';
    let resStr = 'Unknown';
    if (dx && dy && dz) {
        const sx = fmt(dx), sy = fmt(dy), sz = fmt(dz);
        resStr = (sx === sy && sy === sz) ? `${sx} ${c.units}` : `${sx} by ${sy} by ${sz} ${c.units}`;
    }
    const size = (nx && ny && nz) ? `${nx} × ${ny} × ${nz}` : 'Unknown';
    const title = meta.title || ((c.fieldUrl || '').split('/').pop() || 'Field');
    const coordName = c.coordinateSystem || '';
    const unitsStr = meta.field_units || (c.units || '');
    return `
        <div style="display:flex; flex-direction:column; gap:6px; min-width: 260px;">
          <div class="coord-header"><i class="fas fa-cube"></i><span>${title}</span></div>
          <div class="coord-row"><span class="coord-key">Coordinate</span><span class="coord-val">${coordName}</span></div>
          <div class="coord-row"><span class="coord-key">Units</span><span class="coord-val">${unitsStr}</span></div>
          <div class="coord-row"><span class="coord-key">X extent</span><span class="coord-val">${extX}</span></div>
          <div class="coord-row"><span class="coord-key">Y extent</span><span class="coord-val">${extY}</span></div>
          <div class="coord-row"><span class="coord-key">Z extent</span><span class="coord-val">${extZ}</span></div>
          <div class="coord-row"><span class="coord-key">Resolution</span><span class="coord-val">${resStr}</span></div>
          <div class="coord-row"><span class="coord-key">Grid size</span><span class="coord-val">${size}</span></div>
          <div class="coord-row"><span class="coord-key">Fields</span><span class="coord-val">${comps}</span></div>
          <div data-camera-pos class="coord-row"><span class="coord-key">Camera</span><span class="coord-val"></span></div>
          ${meta.doi ? renderProvenanceRow(meta.doi) : ''}
          ${meta.summary ? `<div class="coord-summary"><div class="coord-summary-title">Summary</div><div class="coord-summary-body">${meta.summary}</div></div>` : ''}
        </div>`;
}

let lastGetCtx: (() => DisplayCtx) | null = null;

export function createCoordinateSystemDisplay(getCtx: () => DisplayCtx): HTMLSpanElement {
    lastGetCtx = getCtx;
    const ctx0 = getCtx();

    const button = document.createElement('button');
    button.id = OVERLAY_ID;
    button.className = 'coord-button';
    button.type = 'button';
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-expanded', 'false');
    button.title = 'Coordinate system — click for details';

    const icon = document.createElement('i');
    icon.className = 'fas fa-crosshairs';
    icon.setAttribute('aria-hidden', 'true');

    const textSpan = document.createElement('span');
    textSpan.id = TEXT_ID;
    textSpan.className = 'coord-summary-text';
    textSpan.textContent = shortPillText(ctx0.coordinateSystem, ctx0.units);

    button.appendChild(icon);
    button.appendChild(textSpan);
    (document.getElementById('bottom-cluster') ?? document.body).appendChild(button);

    const popover = document.createElement('div');
    popover.id = POPOVER_ID;
    popover.className = 'coord-popover';
    popover.setAttribute('role', 'dialog');
    popover.setAttribute('aria-label', 'Coordinate system details');
    const details = document.createElement('div');
    details.id = DETAILS_ID;
    popover.appendChild(details);
    document.body.appendChild(popover);

    // Close when a pointer lands outside the popover and button, or on Escape.
    // Listeners live only while open (attached in setOpen(true)).
    const onOutside = (e: PointerEvent): void => {
        const t = e.target as Node | null;
        if (t && (popover.contains(t) || button.contains(t))) return;
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') setOpen(false);
    };

    const setOpen = (open: boolean): void => {
        const isOpen = popover.classList.contains('open');
        if (open === isOpen) return;
        if (open) {
            try { details.innerHTML = renderDetailsHTML(getCtx()); }
            catch { details.innerHTML = '<div style="opacity:0.7;">No metadata available</div>'; }
            // The button now lives in a flex cluster with a dynamic left, so
            // pin the popover to the button's current viewport x each open.
            popover.style.left = `${Math.round(button.getBoundingClientRect().left)}px`;
            popover.classList.add('open');
            // Lift above every floating window so the details are never occluded.
            raisePane(popover);
            button.setAttribute('aria-expanded', 'true');
            // Safe to attach synchronously: the opening interaction's pointerdown
            // already fired before this click handler, so onOutside won't catch it.
            document.addEventListener('pointerdown', onOutside, true);
            document.addEventListener('keydown', onKey);
        } else {
            popover.classList.remove('open');
            button.setAttribute('aria-expanded', 'false');
            document.removeEventListener('pointerdown', onOutside, true);
            document.removeEventListener('keydown', onKey);
        }
    };

    const onClick = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!popover.classList.contains('open'));
    };
    button.addEventListener('click', onClick);

    return textSpan;
}

interface UpdateCtx {
    coordinateSystem?: string;
    units: string;
    camera?: THREE.Camera | null;
    threeJSToPhysical?: (x: number, y: number, z: number) => { x: number; y: number; z: number };
}

export function updateCoordinateSystemDisplay(ctx: UpdateCtx): void {
    const overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) return;
    const textSpan = document.getElementById(TEXT_ID);
    if (textSpan) textSpan.textContent = shortPillText(ctx.coordinateSystem ?? '', ctx.units);
    updateCoordinateCameraDetails(ctx);
}

function isPopoverOpen(): boolean {
    const popover = document.getElementById(POPOVER_ID);
    return !!popover && popover.classList.contains('open');
}

export function updateCoordinateCameraDetails(ctx: UpdateCtx): void {
    try {
        if (!isPopoverOpen()) return;
        const details = document.getElementById(DETAILS_ID);
        if (!details) return;
        if (typeof ctx.threeJSToPhysical !== 'function' || !ctx.camera) return;
        const camPhys = ctx.threeJSToPhysical(ctx.camera.position.x, ctx.camera.position.y, ctx.camera.position.z);
        const row = details.querySelector('[data-camera-pos]');
        if (!row) return;
        const valueEl = row.querySelector('.coord-val');
        if (!valueEl) return;
        valueEl.textContent = `${camPhys.x.toFixed(2)}, ${camPhys.y.toFixed(2)}, ${camPhys.z.toFixed(2)} ${ctx.units}`;
    } catch (_) {}
}

export function isOverlayMounted(): boolean {
    return !!document.getElementById(OVERLAY_ID);
}

export function refreshOpenDetails(): void {
    const details = document.getElementById(DETAILS_ID);
    if (!details || !isPopoverOpen() || !lastGetCtx) return;
    try { details.innerHTML = renderDetailsHTML(lastGetCtx()); }
    catch { /* keep stale content rather than wiping it */ }
}
